// Declarative field editor for CMS blocks (motiflink §2.4).
//
// Renders one `FieldSpec` tree from BLOCK_SCHEMAS against the block's
// `content` + `translations` pair. The `-` filename prefix keeps it out of the
// generated route tree — same convention as `-content-forms.tsx`.
//
// ── Why extractField/patchField are adapted, not called directly ────────────
//
// packages/ui's bilingual helpers assume ONE JSONB column shaped
// `{ ar: '…', en: '…' }`. The CMS overlay model is different: Arabic lives in
// `content[key]` (unwrapped, because the renderer and the public API read it
// as the base value) and English lives in `translations.en[key]`. There is no
// single object to hand to `patchField`.
//
// So `asBilingual()` projects the split pair INTO the `{ar, en}` shape purely
// so <BilingualTabs> can draw its empty-dot indicator, and writes go through
// `writeLocale()`, which reimplements patchField's one real invariant on the
// split shape: a blank value has exactly ONE representation. On the Arabic
// side that is the empty string (`content` keys are always present — the API's
// Zod schema expects them); on the English side it is ABSENCE, because
// `resolveBlockContent` treats a null/empty overlay value as "fall through to
// Arabic" and an overlay key that lingers as `''` would blank the element.
//
// `isFieldEmpty` is not used: it takes the same combined shape, and the
// per-locale emptiness question is already answered by extractField === ''.

import type { Locale } from '@fajrarabi/i18n/locales';
import { BilingualTabs, Button, Input, Textarea, extractField } from '@fajrarabi/ui';
import type { BilingualValue } from '@fajrarabi/ui';

import type { FieldSpec } from '~/lib/block-schemas';

// ─── Path plumbing ──────────────────────────────────────────────────────────
//
// A path is the address of one leaf inside `content`, e.g.
// `['tiers', 0, 'cta', 0, 'label']`. The SAME path addresses the English value
// inside `translations.en`, which is precisely what makes the parallel-array
// overlay of motiflink §2.2 work: `translations.en.items[2].title` overrides
// `content.items[2].title` by index.

export type PathSegment = string | number;
export type Path = PathSegment[];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Read a leaf at `path`. Returns undefined for any missing link in the chain. */
export function readPath(root: unknown, path: Path): unknown {
  let cursor: unknown = root;
  for (const segment of path) {
    if (typeof segment === 'number') {
      if (!Array.isArray(cursor)) return undefined;
      cursor = cursor[segment];
    } else {
      if (!isRecord(cursor)) return undefined;
      cursor = cursor[segment];
    }
  }
  return cursor;
}

/**
 * Immutably write `value` at `path`, creating the containers on the way down.
 * Passing `REMOVE` deletes the key (objects) or leaves a hole as `undefined`
 * (arrays — callers never remove array elements this way; list add/remove
 * rewrites the whole array).
 */
export const REMOVE = Symbol('remove');

// `unknown` already subsumes typeof REMOVE; spelling the union out made the
// intent readable but is redundant to the type system.
export function writePath<T>(root: T, path: Path, value: unknown): T {
  if (path.length === 0) return (value === REMOVE ? undefined : value) as T;

  const [head, ...rest] = path as [PathSegment, ...Path];

  if (typeof head === 'number') {
    const source = Array.isArray(root) ? (root as unknown[]) : [];
    const next = source.slice();
    // Widen holes to `undefined` so a sparse write doesn't produce a sparse
    // array that JSON.stringify turns into `null`s.
    while (next.length <= head) next.push(undefined);
    next[head] = writePath(next[head], rest, value);
    return next as unknown as T;
  }

  const source = isRecord(root) ? root : {};
  const next: Record<string, unknown> = { ...source };
  if (rest.length === 0) {
    if (value === REMOVE) delete next[head];
    else next[head] = value;
  } else {
    next[head] = writePath(next[head], rest, value);
  }
  return next as unknown as T;
}

// ─── The content/translations pair ──────────────────────────────────────────

export interface BlockDraft {
  content: Record<string, unknown>;
  settings: Record<string, unknown>;
  translations: Record<string, Record<string, unknown>>;
}

const OVERLAY_LOCALE = 'en';

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * Project the split pair into the `{ar, en}` shape <BilingualTabs> expects.
 * Read-only — writes go through `writeLocale`.
 */
function asBilingual(draft: BlockDraft, path: Path): BilingualValue {
  const ar = asString(readPath(draft.content, path));
  const en = asString(readPath(draft.translations[OVERLAY_LOCALE], path));
  const value: Partial<Record<Locale, string>> = {};
  if (ar !== '') value.ar = ar;
  if (en !== '') value.en = en;
  return Object.keys(value).length === 0 ? null : value;
}

/** Write one locale of one bilingual leaf, preserving the empty-is-one-thing rule. */
export function writeLocale(
  draft: BlockDraft,
  path: Path,
  locale: Locale,
  next: string,
): BlockDraft {
  if (locale === 'ar') {
    // Arabic is the base value: the key stays present even when blank, because
    // the API validates `content` against the full block schema.
    return { ...draft, content: writePath(draft.content, path, next) };
  }

  const overlay = draft.translations[OVERLAY_LOCALE] ?? {};
  // Emptied English → remove the key so the renderer falls through to Arabic.
  const updated = writePath(overlay, path, next.trim() === '' ? REMOVE : next);
  return { ...draft, translations: { ...draft.translations, [OVERLAY_LOCALE]: updated } };
}

/** Write a language-neutral leaf. Only ever touches `content`. */
export function writeNeutral(draft: BlockDraft, path: Path, next: unknown): BlockDraft {
  return { ...draft, content: writePath(draft.content, path, next) };
}

/**
 * Rewrite a list at `path` in BOTH content and the overlay, keeping the two
 * arrays the same length. This is the invariant that makes index-based overlay
 * resolution safe: deleting content item 1 without deleting overlay item 1
 * would slide every later English string onto the wrong Arabic row.
 *
 * The overlay gets its OWN seed value for inserts. A new row must not mirror
 * content's blank item into the overlay: a CTA's blank item carries `href` and
 * `variant`, and `blockTranslationsByType` is a `strictObject` that REJECTS
 * those language-neutral keys in an overlay. An empty object (or `undefined`
 * for a scalar row) is the only overlay shape that means "not translated yet".
 */
export function writeListLength(
  draft: BlockDraft,
  path: Path,
  mutate: (items: unknown[], seed: 'content' | 'overlay') => unknown[],
): BlockDraft {
  const contentItems = readPath(draft.content, path);
  const nextContent = mutate(Array.isArray(contentItems) ? contentItems : [], 'content');

  const overlay = draft.translations[OVERLAY_LOCALE] ?? {};
  const overlayItems = readPath(overlay, path);
  const nextOverlay = mutate(Array.isArray(overlayItems) ? overlayItems : [], 'overlay');

  return {
    content: writePath(draft.content, path, nextContent),
    settings: draft.settings,
    translations: {
      ...draft.translations,
      [OVERLAY_LOCALE]: writePath(overlay, path, nextOverlay),
    },
  };
}

// ─── Field rendering ────────────────────────────────────────────────────────

interface FieldEditorProps {
  spec: FieldSpec;
  path: Path;
  draft: BlockDraft;
  onChange: (next: BlockDraft) => void;
  /** Nesting depth — drives the indentation of list groups. */
  depth?: number;
}

export function FieldEditor({ spec, path, draft, onChange, depth = 0 }: FieldEditorProps) {
  if (spec.kind === 'list' && spec.object) {
    return <ObjectField spec={spec} path={path} draft={draft} onChange={onChange} depth={depth} />;
  }
  if (spec.kind === 'list') {
    return <ListField spec={spec} path={path} draft={draft} onChange={onChange} depth={depth} />;
  }
  if (spec.bilingual) {
    return <BilingualField spec={spec} path={path} draft={draft} onChange={onChange} />;
  }
  return <NeutralField spec={spec} path={path} draft={draft} onChange={onChange} />;
}

function FieldLabel({ spec }: { spec: FieldSpec }) {
  return (
    <span className="flex items-baseline gap-2">
      <span className="text-sm font-semibold text-admin-900">{spec.label}</span>
      {spec.required && (
        <span className="text-[11px] font-medium uppercase tracking-wide text-admin-400">
          required
        </span>
      )}
    </span>
  );
}

function FieldHelp({ spec }: { spec: FieldSpec }) {
  if (!spec.help) return null;
  return <p className="mt-1 text-xs leading-relaxed text-admin-500">{spec.help}</p>;
}

// ── bilingual leaf ──

function BilingualField({ spec, path, draft, onChange }: Omit<FieldEditorProps, 'depth'>) {
  const value = asBilingual(draft, path);
  const long = spec.kind === 'textarea' || spec.kind === 'richtext';

  return (
    <div>
      <FieldLabel spec={spec} />
      <FieldHelp spec={spec} />
      <BilingualTabs
        className="mt-2"
        value={value}
        ariaLabel={`${spec.label} language`}
        onChange={(locale, next) => onChange(writeLocale(draft, path, locale, next))}
      >
        {({ locale, value: localeValue, dir, id, onChange: setValue }) =>
          long ? (
            <Textarea
              id={id}
              dir={dir}
              rows={spec.kind === 'richtext' ? 14 : 3}
              value={localeValue}
              placeholder={spec.placeholder}
              onChange={(e) => setValue(e.target.value)}
              className={spec.kind === 'richtext' ? 'font-mono text-xs leading-relaxed' : 'text-sm'}
            />
          ) : (
            <Input
              id={id}
              dir={dir}
              value={localeValue}
              placeholder={spec.placeholder}
              onChange={(e) => setValue(e.target.value)}
              className="text-sm"
              aria-label={`${spec.label} (${locale})`}
            />
          )
        }
      </BilingualTabs>
      {/* An English override that equals the Arabic base is almost always a
          copy-paste slip — worth a nudge, never a block. */}
      {extractField(value, 'ar') !== '' &&
        extractField(value, 'ar') === extractField(value, 'en') && (
          <p className="mt-1 text-xs text-sun">
            The English override is identical to the Arabic. Clear it to fall through instead.
          </p>
        )}
    </div>
  );
}

// ── language-neutral leaf ──

function NeutralField({ spec, path, draft, onChange }: Omit<FieldEditorProps, 'depth'>) {
  const raw = readPath(draft.content, path);
  const set = (next: unknown) => onChange(writeNeutral(draft, path, next));

  if (spec.kind === 'boolean') {
    return (
      <label className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={raw === true}
          onChange={(e) => set(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-admin-300 text-brand-500 focus:ring-brand-500"
        />
        <span>
          <FieldLabel spec={spec} />
          <FieldHelp spec={spec} />
        </span>
      </label>
    );
  }

  if (spec.kind === 'select' || spec.kind === 'color' || spec.kind === 'icon') {
    const options = spec.options ?? [];
    // `columns` is numeric in the schema but a string in <option value>.
    const numeric = typeof raw === 'number';
    return (
      <label className="block">
        <FieldLabel spec={spec} />
        <FieldHelp spec={spec} />
        <select
          value={
            raw === null || raw === undefined
              ? ''
              : // eslint-disable-next-line @typescript-eslint/no-base-to-string
                String(raw)
          }
          onChange={(e) => {
            const v = e.target.value;
            if (v === '') set(null);
            else if (numeric || (spec.key === 'columns' && /^\d+$/.test(v))) set(Number(v));
            else set(v);
          }}
          className="mt-2 block h-10 w-full rounded-2xl border border-burgundy-500/15 bg-cream-50 px-3 text-sm text-ink-900"
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
    );
  }

  if (spec.kind === 'number') {
    return (
      <label className="block">
        <FieldLabel spec={spec} />
        <FieldHelp spec={spec} />
        <Input
          type="number"
          min={spec.min}
          max={spec.max}
          value={typeof raw === 'number' ? String(raw) : ''}
          onChange={(e) => set(e.target.value === '' ? null : Number(e.target.value))}
          className="mt-2 text-sm"
        />
      </label>
    );
  }

  // text / image / anything else → a plain single-line input. Neutral values
  // are LTR by definition (URLs, slugs, digits) except `glyph`, which is a
  // single Arabic letter and reads better in its own direction.
  const isGlyph = spec.key === 'glyph';
  const value = asString(raw);
  return (
    <label className="block">
      <FieldLabel spec={spec} />
      <FieldHelp spec={spec} />
      <Input
        dir={isGlyph ? 'auto' : 'ltr'}
        value={value}
        placeholder={spec.placeholder}
        onChange={(e) => set(e.target.value)}
        className={isGlyph ? 'mt-2 text-lg' : 'mt-2 font-mono text-xs'}
      />
      {spec.kind === 'image' && value !== '' && (
        <img
          src={value}
          alt=""
          className="mt-2 h-16 w-16 rounded-full object-cover ring-1 ring-admin-200"
        />
      )}
    </label>
  );
}

// ── nested object (a CTA, the newsletter form) ──

function ObjectField({ spec, path, draft, onChange, depth = 0 }: FieldEditorProps) {
  const itemFields = spec.itemFields ?? [];
  const present = isRecord(readPath(draft.content, path));

  function seed(): Record<string, unknown> {
    const next: Record<string, unknown> = {};
    for (const field of itemFields) {
      if (field.options && field.options.length > 0) next[field.key] = field.options[0]!.value;
      else next[field.key] = '';
    }
    return next;
  }

  /** Removing the group must clear its overlay too, or the API sees a stray `{en:{cta:{…}}}`. */
  function removeGroup() {
    const overlay = draft.translations[OVERLAY_LOCALE] ?? {};
    onChange({
      ...draft,
      content: writePath(draft.content, path, REMOVE),
      translations: { ...draft.translations, [OVERLAY_LOCALE]: writePath(overlay, path, REMOVE) },
    });
  }

  return (
    <fieldset className="rounded-2xl border border-admin-200 bg-admin-50/40 p-4">
      <legend className="flex items-center gap-3 px-1">
        <FieldLabel spec={spec} />
        {spec.optional && (
          <button
            type="button"
            onClick={() => (present ? removeGroup() : onChange(writeNeutral(draft, path, seed())))}
            className="rounded-lg px-2 py-0.5 text-xs font-medium text-admin-600 transition-colors hover:bg-admin-100"
          >
            {present ? 'Remove' : 'Add'}
          </button>
        )}
      </legend>
      <FieldHelp spec={spec} />

      {present ? (
        <div className="mt-3 space-y-4">
          {itemFields.map((field) => (
            <FieldEditor
              key={field.key}
              spec={field}
              path={[...path, field.key]}
              draft={draft}
              onChange={onChange}
              depth={depth + 1}
            />
          ))}
        </div>
      ) : (
        <p className="mt-3 rounded-xl border border-dashed border-admin-200 px-4 py-4 text-center text-xs text-admin-500">
          Not set — this section is omitted from the page.
        </p>
      )}
    </fieldset>
  );
}

// ── list ──

function ListField({ spec, path, draft, onChange, depth = 0 }: FieldEditorProps) {
  const itemFields = spec.itemFields ?? [];
  const raw = readPath(draft.content, path);
  const items = Array.isArray(raw) ? raw : [];

  const fixed = spec.fixedLength !== undefined;
  const atMin = items.length <= (spec.fixedLength ?? spec.minItems ?? 0);
  const atMax = items.length >= (spec.fixedLength ?? spec.maxItems ?? Infinity);

  // A list of scalars (`bullets`, `features`) is modelled as itemFields with a
  // single empty-key spec — its path is the index itself, with no leaf key.
  const scalarSpec = itemFields.length === 1 && itemFields[0]!.key === '' ? itemFields[0]! : null;

  function blankItem(): unknown {
    if (scalarSpec) return '';
    const item: Record<string, unknown> = {};
    for (const field of itemFields) {
      if (field.kind === 'list') item[field.key] = [];
      else if (field.kind === 'boolean') item[field.key] = false;
      else if (field.options && field.options.length > 0) item[field.key] = field.options[0]!.value;
      else item[field.key] = '';
    }
    return item;
  }

  const move = (from: number, to: number) => {
    if (to < 0 || to >= items.length) return;
    onChange(
      writeListLength(draft, path, (arr) => {
        const next = arr.slice();
        // The overlay array can legitimately be shorter than content's — pad
        // before swapping so indexes keep pointing at the same row.
        while (next.length < items.length) next.push(undefined);
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        return next;
      }),
    );
  };

  return (
    <fieldset className="rounded-2xl border border-admin-200 bg-admin-50/40 p-4">
      <legend className="px-1">
        <FieldLabel spec={spec} />
      </legend>
      <FieldHelp spec={spec} />

      <div className="mt-3 space-y-3">
        {items.length === 0 && (
          <p className="rounded-xl border border-dashed border-admin-200 px-4 py-5 text-center text-xs text-admin-500">
            Nothing here yet.
          </p>
        )}

        {items.map((_item, index) => (
          <div
            key={index}
            className="rounded-xl border border-admin-200 bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04)]"
          >
            <div className="mb-3 flex items-center justify-between gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-admin-400">
                {spec.label} {index + 1}
              </span>
              {!fixed && (
                <div className="flex items-center gap-1">
                  <IconButton
                    label={`Move ${spec.label} ${index + 1} up`}
                    disabled={index === 0}
                    onClick={() => move(index, index - 1)}
                  >
                    ↑
                  </IconButton>
                  <IconButton
                    label={`Move ${spec.label} ${index + 1} down`}
                    disabled={index === items.length - 1}
                    onClick={() => move(index, index + 1)}
                  >
                    ↓
                  </IconButton>
                  <button
                    type="button"
                    disabled={atMin}
                    onClick={() =>
                      onChange(
                        writeListLength(draft, path, (arr) => {
                          const next = arr.slice();
                          while (next.length < items.length) next.push(undefined);
                          next.splice(index, 1);
                          return next;
                        }),
                      )
                    }
                    className="rounded-lg px-2 py-1 text-xs font-medium text-heart transition-colors hover:bg-heart/10 disabled:opacity-30"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            {scalarSpec ? (
              <FieldEditor
                spec={{ ...scalarSpec, key: String(index), label: `${spec.label} ${index + 1}` }}
                path={[...path, index]}
                draft={draft}
                onChange={onChange}
                depth={depth + 1}
              />
            ) : (
              <div className="space-y-4">
                {itemFields.map((field) => (
                  <FieldEditor
                    key={field.key}
                    spec={field}
                    path={[...path, index, field.key]}
                    draft={draft}
                    onChange={onChange}
                    depth={depth + 1}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {!fixed && (
        <div className="mt-3 flex items-center gap-3">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={atMax}
            onClick={() =>
              onChange(
                writeListLength(draft, path, (arr, seed) => {
                  const next = arr.slice();
                  while (next.length < items.length) next.push(undefined);
                  // Content gets a structurally complete blank row; the
                  // overlay gets "nothing translated yet".
                  next.push(seed === 'content' ? blankItem() : scalarSpec ? undefined : {});
                  return next;
                }),
              )
            }
          >
            Add {spec.label.toLowerCase().replace(/s$/, '')}
          </Button>
          <span className="text-xs text-admin-500">
            {items.length}
            {spec.maxItems !== undefined ? ` of ${spec.maxItems}` : ''}
            {spec.minItems !== undefined && items.length < spec.minItems
              ? ` — at least ${spec.minItems} required`
              : ''}
          </span>
        </div>
      )}
    </fieldset>
  );
}

function IconButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="grid h-7 w-7 place-items-center rounded-lg text-sm text-admin-500 transition-colors hover:bg-admin-100 disabled:opacity-30"
    >
      {children}
    </button>
  );
}
